import React, { FC } from 'react'
import { useParams } from 'react-router-dom'
import { useStatus } from '@sketch-hq/sketch-web-renderer'

import { IS_EMBEDDED } from '@sketch/constants'
import {
  Navbar,
  useForDesktop,
  useForTablet,
  IsEmbedded,
  NotEmbedded,
  ModalRestrictor,
} from '@sketch/components'
import { RouteParams, routes, useFlag } from '@sketch/modules-common'
import { Zoom } from 'components/Zoom'
import { ShareWithoutVersion } from 'modules/versioning'
import { usePlaceDraftAnnotation } from 'modules/annotations/hooks'

import HeaderNavigation from '../../../components/HeaderNavigation'
import HeaderSidebarToggle from '../../../components/HeaderSidebarToggle'
import {
  EditInSketchButton,
  OpenTemplateButton,
} from '../../../components/EditInSketchButton'
import VersionLinkButton from '../../../components/VersionLinkButton'
import DocumentNavbar from '../../../components/DocumentNavbar'
import DocumentSettingsDropdown from '../../../components/DocumentHeader/DocumentSettingsDropdown'
import { MIN_ZOOM, MAX_ZOOM } from 'components/WebRendererCanvas/constants'
import { DocumentActions } from './DocumentActions'

import {
  MiddleContainer,
  NavbarItem,
  ActionsNavbarItem,
  AddAnnotationText,
} from './PageCanvasViewHeader.styles'

import { AddAnnotationNavbarItem } from 'modules/annotations/components'
import { DocumentSidebarLayoutExtraProps } from 'modules/shares/components/DocumentSidebarLayout'
import { useCanvasZoom } from 'components/WebRendererCanvas/hooks'

type PageCanvasViewHeaderProps = {
  share: ShareWithoutVersion
  isViewingLatestVersion: boolean
  userCanOpenInApp: boolean
} & Pick<
  DocumentSidebarLayoutExtraProps,
  'setSidebarLeftOpen' | 'toggleSidebarRight' | 'isSidebarRightOpen'
>

export const PageCanvasViewHeader: FC<PageCanvasViewHeaderProps> = ({
  share,
  toggleSidebarRight,
  isSidebarRightOpen,
  isViewingLatestVersion,
  userCanOpenInApp,
}) => {
  const { type } = share

  const isSetAsTemplateFFEnabled = useFlag('set-as-template')
  const { pageUUID } = useParams<RouteParams<'SHARE_PAGE_CANVAS_VIEW'>>()
  const isDesktop = useForDesktop()
  const isTabletAndBigger = useForTablet()
  const canOpenInApp = userCanOpenInApp && isDesktop && !IS_EMBEDDED
  const showLatestVersionButton =
    isTabletAndBigger && !isViewingLatestVersion && !IS_EMBEDDED

  const [zoom, setZoom] = useCanvasZoom()
  const [placeDraftAnnotation] = usePlaceDraftAnnotation() || []
  const canvasStatus = useStatus()
  const isTemplate = type === 'TEMPLATE'

  if (placeDraftAnnotation && isTabletAndBigger && !IS_EMBEDDED) {
    return (
      <>
        <MiddleContainer />
        <AddAnnotationText>Click anywhere to comment</AddAnnotationText>
        <MiddleContainer>
          <NavbarItem>
            <Zoom
              zoom={zoom}
              setZoom={setZoom}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
            />
          </NavbarItem>
          <AddAnnotationNavbarItem disabled={canvasStatus?.type !== 'READY'} />
        </MiddleContainer>
        <HeaderSidebarToggle
          toggleSidebar={toggleSidebarRight}
          isSidebarOpen={isSidebarRightOpen}
        />
      </>
    )
  }

  return (
    <>
      <NotEmbedded>
        <Navbar.Section>
          <HeaderNavigation share={share} />
        </Navbar.Section>

        <Navbar.FixedWidthSection align="end">
          {isTabletAndBigger && (
            <>
              <NavbarItem>
                <DocumentNavbar />
              </NavbarItem>
              <NavbarItem>
                <Zoom
                  zoom={zoom}
                  setZoom={setZoom}
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                />
              </NavbarItem>
            </>
          )}

          {canOpenInApp && isTemplate && isSetAsTemplateFFEnabled && (
            <NavbarItem>
              <OpenTemplateButton
                shareId={share.identifier}
                disabled={!isViewingLatestVersion}
              />
            </NavbarItem>
          )}

          {canOpenInApp && !isTemplate && (
            <NavbarItem>
              <EditInSketchButton
                shareId={share.identifier}
                disabled={!isViewingLatestVersion}
              />
            </NavbarItem>
          )}

          {showLatestVersionButton && (
            <NavbarItem>
              <VersionLinkButton
                to={routes.SHARE_PAGE_CANVAS_VIEW.create({
                  shareID: share.identifier,
                  pageUUID: pageUUID,
                })}
              >
                See Latest Version
              </VersionLinkButton>
            </NavbarItem>
          )}

          {isTabletAndBigger && <DocumentActions />}

          <ActionsNavbarItem>
            {isTabletAndBigger && (
              <AddAnnotationNavbarItem
                disabled={canvasStatus?.type !== 'READY'}
              />
            )}
            <ModalRestrictor>
              <DocumentSettingsDropdown />
            </ModalRestrictor>
          </ActionsNavbarItem>

          <HeaderSidebarToggle
            toggleSidebar={toggleSidebarRight}
            isSidebarOpen={isSidebarRightOpen}
          />
        </Navbar.FixedWidthSection>
      </NotEmbedded>

      <IsEmbedded>
        <Navbar.Section>
          <HeaderNavigation share={share} />
        </Navbar.Section>

        <Navbar.FixedWidthSection align="end">
          <NavbarItem>
            <Zoom
              zoom={zoom}
              setZoom={setZoom}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
            />
          </NavbarItem>
        </Navbar.FixedWidthSection>
      </IsEmbedded>
    </>
  )
}

export default PageCanvasViewHeader
